<script lang="ts">
	import { GeocodeAddressDocument } from '$lib/queries/generated/GeocodeAddress'
	import { getContext } from 'svelte'
	import type { MutateTripContext } from '$lib/context/async-operations'
	import modal from '$src/lib/stores/modal-state'
	import { urlState } from '$src/lib/stores/url-state'
	import { browser } from '$app/environment'
	import { getContextClient } from '@urql/svelte'
	import Button from '$components/buttons/Button.svelte'
	import debounce from 'lodash-es/debounce'
	import Spinner from '$components/loading/Spinner.svelte'
	import Modal from './Modal.svelte'
	import type { GeoPointFieldsFragment } from '$src/lib/queries/fragments/generated/GeoPointFields'

	const mutateTrip = getContext<MutateTripContext>('mutateTrip')

	let { blockId } = $derived($urlState)

	const closeModal = () => {
		modal.close()
	}

	let address = $state('')
	let geocodeResult = $state<GeoPointFieldsFragment | null>(null)
	let isLoading = $state(false)
	let errorMessage = $state('')

	const client = getContextClient()

	async function fetchGeocodeData() {
		if (!browser) return
		if (address == '') {
			errorMessage = 'Please enter an address.'
			geocodeResult = null
			return
		}

		try {
			isLoading = true
			errorMessage = ''

			const result = await client.query(GeocodeAddressDocument, {
				query: address,
			})

			if (result.data?.geocodeAddress == null || result.data?.geocodeAddress?.address === '') {
				errorMessage = 'Unable to geolocate that address. Please try again.'
			} else {
				geocodeResult = result.data?.geocodeAddress
				errorMessage = ''
			}
		} catch (error) {
			errorMessage = 'Failed to fetch URL data. Please check the URL and try again.'
		} finally {
			isLoading = false
		}
	}

	const saveAddress = async () => {
		if (geocodeResult && errorMessage == '') {
			mutateTrip.updateLocationForBlock(blockId, {
				lat: geocodeResult.latitude,
				lng: geocodeResult.longitude,
				...(geocodeResult.city ? { city: geocodeResult.city } : {}),
				...(geocodeResult.state ? { state: geocodeResult.state } : {}),
				...(geocodeResult.country ? { country: geocodeResult.country } : {}),
				...(geocodeResult.countryCode ? { countryCode: geocodeResult.countryCode } : {}),
				...(geocodeResult.id ? { geoId: geocodeResult.id } : {}),
				...(geocodeResult.isOsm ? { isOsm: geocodeResult.isOsm } : {}),
				...(geocodeResult.address ? { address: geocodeResult.address } : {}),
				...(geocodeResult.name ? { shortName: geocodeResult.name } : {}),
			})
		} else {
			alert('Could not verify address.')
		}

		closeModal()
	}

	// debounce fetchGeocodeData
	const debouncedFetchGeocodeData = debounce(fetchGeocodeData, 500)
</script>

<Modal open={true} isLightOverride={false} size="sm:max-w-xl" title="Edit Address">
	<div class="flex flex-col gap-2">
		<textarea
			class="w-full p-2 border rounded bg-transparent text-sm dark:text-brand-gray-1 placeholder:text-brand-gray-3 dark:placeholder:text-brand-gray-3 focus:text-brand-gray-1 dark:focus:text-brand-gray-1 focus:border-brand-gray-1 outline-none focus:outline-none focus:ring-0"
			placeholder="Enter the new address"
			onblur={debouncedFetchGeocodeData}
			onchange={debouncedFetchGeocodeData}
			bind:value={address}
		>
			{address}
		</textarea>
		<p class="text-xs h-4 flex items-center gap-2">
			{#if isLoading}
				<Spinner size="sm" />
				<span class="text-brand-gray-3">Verifying address...</span>
			{:else}
				<span class="text-red-600">{errorMessage ?? ''}</span>
			{/if}
		</p>

		<div class="flex justify-end my-4">
			<Button
				theme="brand"
				size="sm"
				class="flex items-center gap-2"
				disabled={isLoading || !geocodeResult || errorMessage != ''}
				onclick={saveAddress}
			>
				Save
			</Button>
		</div>
	</div></Modal
>
